// UI atom is updated via setUi aggregator supplied by the batch processor
import type { EventTuple, PaintStroke } from './types'
import { blendMutable, writeFn } from './layerSystem'
import { recordMutationCandidate } from '@/events/mutationJournal'
import { normalToFace, faceToNormal } from '../voxelLiterals'
import { VoxelData, Face, VoxelMap } from '@/voxelTypes'
import { posKeyXYZ, parseKeyX, parseKeyY, parseKeyZ, posAdd, offsetVoxels, type Position, posEqual, getBoundingBoxFromKeys, shiftKeys } from '../voxelMath'
import { placementPolicy, usesSurfaceOrientation } from '../voxels/registry'
import { rasterizeShape } from '../shapes'
import type { SnapshotMeta } from '@/document/model'
import { floodFillSelect, calculateBoundingBoxCenter, selectVoxelsInBounds } from '../helpers/selectionHelpers'
import { selectionSubjectId } from '@/subjects/selection'
import { advanceAnchor, applyExtrusionFromFace, applyExtrusionFromKeys } from '../interactions/extrusionHelpers'
import { findContiguousFace } from '../interactions/faceHelpers'
import { Vector3 } from '@/math'
import { rectangleVoxels, expandScribble } from '@/events/ops'
import { computePaintStroke as computePaintFromSpine, computePaintBucket } from '@/interactions/paintHelpers'
import { type PaintSettings, type ScribbleSettings } from '@/uiState'
import type { Spine } from '@/interactions/spineCore'
import { ToolType } from '@/events/types'
import { isChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import { translateGrid } from '@/storage/grid/gridTransform'
import { brushStrokeBounds, brushStrokeHandlers, isBrushStrokeEventType, type BrushStrokePayload } from '@/events/brushStroke'
import { pageEventHandlers } from '@/pages/events'

type Setter = any

// ----------------------------------------------------------------------------
// Bounds inference from events
// ----------------------------------------------------------------------------

export function computeBoundsFromEvents(events: EventTuple[]): Position | undefined {
  let minX = Number.POSITIVE_INFINITY, minY = Number.POSITIVE_INFINITY, minZ = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY, maxY = Number.NEGATIVE_INFINITY, maxZ = Number.NEGATIVE_INFINITY
  let seen = false

  for (const ev of events) {
    const type = String(ev[0])
    if (type === 'voxel.blend') {
      const payload = ev[1] as any
      if (payload && typeof payload.forEach === 'function') {
        (payload as Map<number, any>).forEach((_, key: number) => {
          const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
          if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return
          seen = true
          if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z
          if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z
        })
      } else if (Array.isArray(payload)) {
        for (const key of payload as number[]) {
          const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
          if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue
          seen = true
          if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z
          if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z
        }
      }
    } else if (type === 'paint.rectangle') {
      const p = ev[1] as any
      const mins = p?.min, maxs = p?.max
      if (mins && maxs) {
        const xs = [Number(mins.x), Number(maxs.x)].filter(Number.isFinite) as number[]
        const ys = [Number(mins.y), Number(maxs.y)].filter(Number.isFinite) as number[]
        const zs = [Number(mins.z), Number(maxs.z)].filter(Number.isFinite) as number[]
        if (xs.length === 2 && ys.length === 2 && zs.length === 2) {
          seen = true
          const lx = Math.min(xs[0], xs[1]), hx = Math.max(xs[0], xs[1])
          const ly = Math.min(ys[0], ys[1]), hy = Math.max(ys[0], ys[1])
          const lz = Math.min(zs[0], zs[1]), hz = Math.max(zs[0], zs[1])
          if (lx < minX) minX = lx; if (ly < minY) minY = ly; if (lz < minZ) minZ = lz
          if (hx > maxX) maxX = hx; if (hy > maxY) maxY = hy; if (hz > maxZ) maxZ = hz
        }
      }
    } else if (type === 'geom.shape') {
      const p = ev[1] as any
      if (p && p.center) {
        const cx = Number(p.center.x), cy = Number(p.center.y), cz = Number(p.center.z)
        const r = Number(p.radius) || 0
        const h = Number(p.height) || 0
        let dx = r, dy = r, dz = r
        if (p.type === 'cylinder' || p.type === 'cone') {
          if (p.axis === 'x') { dx = h; dy = r; dz = r }
          else if (p.axis === 'y' || !p.axis) { dy = h; dx = r; dz = r }
          else if (p.axis === 'z') { dz = h; dx = r; dy = r }
        }
        const lx = cx - dx, hx = cx + dx
        const ly = cy - dy, hy = cy + dy
        const lz = cz - dz, hz = cz + dz
        if ([lx, ly, lz, hx, hy, hz].every(Number.isFinite)) {
          seen = true
          if (lx < minX) minX = lx; if (ly < minY) minY = ly; if (lz < minZ) minZ = lz
          if (hx > maxX) maxX = hx; if (hy > maxY) maxY = hy; if (hz > maxZ) maxZ = hz
        }
      }
    } else if (type === 'scribble.apply' || type === 'scribble.append') {
      const p = ev[1] as any
      const spine = p?.spine
      const radius = Math.max(0, Number(p?.scribble?.radius) || 0)
      if (Array.isArray(spine)) {
        for (const point of spine) {
          const key = Number(point?.key)
          if (!Number.isFinite(key)) continue
          const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
          if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue
          seen = true
          // Account for scribble radius expanding in all directions
          const lx = x - radius, hx = x + radius
          const ly = y - radius, hy = y + radius
          const lz = z - radius, hz = z + radius
          if (lx < minX) minX = lx; if (ly < minY) minY = ly; if (lz < minZ) minZ = lz
          if (hx > maxX) maxX = hx; if (hy > maxY) maxY = hy; if (hz > maxZ) maxZ = hz
        }
      }
    } else if (isBrushStrokeEventType(type)) {
      const box = brushStrokeBounds(ev[1] as BrushStrokePayload)
      if (box) {
        seen = true
        if (box.min.x < minX) minX = box.min.x; if (box.min.y < minY) minY = box.min.y; if (box.min.z < minZ) minZ = box.min.z
        if (box.max.x > maxX) maxX = box.max.x; if (box.max.y > maxY) maxY = box.max.y; if (box.max.z > maxZ) maxZ = box.max.z
      }
    }
  }

  if (seen) {
    const x = Math.max(1, Math.floor(maxX) + 1)
    const y = Math.max(1, Math.floor(maxY) + 1)
    const z = Math.max(1, Math.floor(maxZ) + 1)
    return { x, y, z }
  }

  return undefined
}

export type HandlerCtx = {
  ui: any
  meta: SnapshotMeta
  set: Setter
  // Queue additional events to run within the current batch
  dispatchEvents: (events: EventTuple[]) => void
  // Working base map (may be original or a clone depending on batch)
  base: VoxelMap
  // Stage UI updates; applied once at end of batch
  setUi: (updater: ((prev: any) => any) | Record<string, any>) => void
  // Stage Meta updates
  setMeta: (updater: ((prev: any) => any) | Record<string, any>) => void
  // Replace base efficiently from a snapshot
  setBase: (next: VoxelMap) => void
  // The document before the stroke being drawn (the app log's draft base) while a draft is open;
  // read by brush coats so a stroke never coats onto its own paint. Absent outside drafts.
  strokeBase?: VoxelMap
}

type Handler = (ctx: HandlerCtx, event: EventTuple) => void

// (combineKeys removed; handlers now build in-place)

// Mutable variant: applies selection commit directly into the provided base layer
function applySelectionToBaseMutable(
  baseLayer: VoxelMap,
  selection: { keys: ReadonlyArray<number>; translate: Position | null },
  bounds: { x: number; y: number; z: number },
  mirrorCtx?: { bounds: Position; mirrors: { x?: number; y?: number; z?: number } } | null,
) {
  // Compute moved clip from base
  const movedClip = offsetVoxels(selection.keys as number[], selection.translate || { x: 0, y: 0, z: 0 }, bounds, baseLayer)
  // Remove original selection voxels from base, then place moved clip
  blendMutable(baseLayer, selection.keys as number[], 'subtract', mirrorCtx || undefined)
  blendMutable(baseLayer, movedClip, 'place', mirrorCtx || undefined)
}

const transformExists = (transform: Position | null | undefined) => {
  return transform && !(posEqual(transform, { x: 0, y: 0, z: 0 }))
}

// Commit any existing selection to the base layer without clearing UI state.
// This mirrors the placement semantics in 'selection.change.clear' but keeps voxelSelection untouched
// so callers can immediately replace it with a new selection.
function commitExistingSelection(ctx: HandlerCtx, ui: any) {
  const sel = ui.voxelSelection
  if (!sel || !sel.keys) return ui
  if (!transformExists(sel.translate)) return ui
  // Schedule a commit event instead of mutating base directly; let the
  // batch runner decide base cloning needs.
  ctx.dispatchEvents([
    ['doc.selection.commit']
  ])
  return ui
}

// Commit any pending face extrusion (based on explicit faceSelection keys + offset)
function commitPendingFaceExtrusion(ctx: HandlerCtx, ui: any) {
  const { dispatchEvents } = ctx
  const faceSel = ui.faceSelection
  const offset = faceSel?.offset || 0
  if (!faceSel || !offset) return ui
  const payload = { face: faceSel.face, keys: Array.from(faceSel.keys as Set<number>), offset, fillMode: !!ui?.tool?.rectangle?.fillMode }
  // Commit via existing handler; it will advance keys and zero the offset
  dispatchEvents([
    ['voxel.extrudeFaces', payload]
  ])
  return ui
}

// Unified preparer for any selection change entrypoint
const flushPendingTransforms = (ctx: HandlerCtx, _op: 'replace' | 'add' | 'subtract' = 'replace', ui: any) => {
  // Always flush pending selection transforms and face extrusions before changing selection state
  ui = commitExistingSelection(ctx, ui)
  ui = commitPendingFaceExtrusion(ctx, ui)
  return ui
}

const computedSelection = (keys: number[] | Set<number> | MapIterator<number>) => {
  keys = Array.isArray(keys) ? keys : Array.from(keys)
  const boundingBox = getBoundingBoxFromKeys(keys)
  const center = calculateBoundingBoxCenter(boundingBox)
  return { keys, boundingBox, center }
}

const finalizeSelection = (ctx: HandlerCtx, _ui: any, keys: Set<number>) => {
  const { setUi } = ctx
  const boundingBox = getBoundingBoxFromKeys(keys)
  const center = calculateBoundingBoxCenter(boundingBox)
  if (boundingBox && center && keys.size > 0) {
    setUi((prev: any) => ({
      ...prev,
      faceSelection: null,
      voxelSelection: {
        ...computedSelection(keys),
        translate: null,
      },
      activeSubjectRef: { kind: 'selection', id: selectionSubjectId }
    }))
  } else {
    setUi(clearSelectionUiState)
  }
}

function clearSelectionUiState(prev: any): any {
  const selectionOwnsTransformState = !!prev?.voxelSelection ||
    !!prev?.faceSelection ||
    prev?.activeSubjectRef?.kind === 'selection'

  if (!selectionOwnsTransformState) {
    return {
      ...prev,
      voxelSelection: null,
      faceSelection: null,
    }
  }

  return {
    ...prev,
    voxelSelection: null,
    faceSelection: null,
    axisDragState: null,
    selectionAxisDragRequest: null,
    rotationDragRequest: null,
    rotationActive: false,
    activeSubjectRef: (prev?.activeSubjectRef?.kind === 'selection') ? null : prev?.activeSubjectRef,
  }
}

type SelectionSource = { kind: 'flood' | 'face' | 'rect' | 'positions';[k: string]: any }
const applySelectionSource = (
  ctx: HandlerCtx,
  params: { source: SelectionSource; op: 'replace' | 'add' | 'subtract' },
  out: Set<number>
) => {
  const { base } = ctx
  const grid: any = base
  const applyOp = (k: number) => {
    if (params.op === 'subtract') out.delete(k)
    else out.add(k)
  }
  const { source } = params
  if (source.kind === 'flood') {
    const { anchor, sameTypeAndColor } = source
    const match = (sameTypeAndColor ?? true)
    floodFillSelect(anchor, grid, match, out, params.op === 'subtract' ? 'subtract' : 'add')
    return
  }
  if (source.kind === 'face') {
    const { anchor, face, sameTypeAndColor } = source
    const n = faceToNormal(face as Face)
    const normal = new Vector3(n.x, n.y, n.z)
    const match = (sameTypeAndColor ?? true)
    const keys: Set<number> = findContiguousFace(anchor, normal, grid, match)
    for (const k of keys) if (grid.has(k)) applyOp(k)
    return
  }
  if (source.kind === 'rect') {
    const { min, max } = source
    selectVoxelsInBounds(min, max, base, ctx.meta.bounds, out, params.op === 'subtract' ? 'subtract' : 'add')
    return
  }
  if (source.kind === 'positions') {
    const { positions } = source
    for (const pos of positions as Position[]) {
      const k = posKeyXYZ(pos.x, pos.y, pos.z)
      if (base.has(k)) applyOp(k)
    }
    return
  }
}

const scribbleApply: Handler = (ctx, event) => {
    const { base, meta } = ctx
    const [, payload] = event as ['scribble.apply' | 'scribble.append', { spine: Spine; scribble: ScribbleSettings; subtract?: boolean; onlyType?: string }]
    const { spine, scribble, subtract, onlyType } = payload
    const r = Math.max(0, scribble?.radius ?? 0)
    const connect = !!scribble?.shouldConnect
    const shape = scribble?.shape
    const placement = scribble?.placement
    const spacingVoxels = scribble?.spacingVoxels
    const connectMinRange = scribble?.connectMinRange
    const bridgeDiagonals = typeof scribble?.bridgeDiagonals === 'boolean' ? !!scribble.bridgeDiagonals : undefined
    expandScribble(
      spine,
      r,
      meta.bounds,
      base,
      !!subtract,
      {
        connect,
        shape,
        placement,
        spacingVoxels,
        connectMinRange,
        bridgeDiagonals,
        onlyType,
      }
    )
    // expandScribble now mutates base directly; no further blending required
  }

// Registry of event handlers
export const handlers: Record<string, Handler> = {
  // ========================
  // System/persistence events
  // ========================

  'voxel.blend': (ctx, event) => {
    const { base, meta } = ctx
    const [, voxels, mode] = event as any
    blendMutable(base, voxels, mode, { bounds: meta.bounds, mirrors: (meta as any).mirrors || {} })
  },

  // ========================
  // Selection events
  // ========================
  // Select all voxels in the base layer
  'selection.change.selectAll': (ctx) => {
    const { base, ui: uiIn } = ctx
    let ui = flushPendingTransforms(ctx, 'replace', uiIn)
    const all = new Set<number>()
    ;(base as any)?.forEach?.((_: any, key: number) => { all.add(key) })
    finalizeSelection(ctx, ui, all)
  },

  'selection.change.floodFillFromPosition': (ctx, event) => {
    const { dispatchEvents } = ctx
    const [, position, opts] = event as any
    const op: 'replace' | 'add' | 'subtract' = (opts?.op || 'replace')
    dispatchEvents([
      ['selection.change', { op, source: { kind: 'flood', anchor: position, sameTypeAndColor: true } } as any]
    ])
  },

  'selection.translate': ({ ui, setUi }, event) => {
    let [, delta] = event as any
    delta = delta || { x: 0, y: 0, z: 0 }
    const sel = ui.voxelSelection
    if (sel && (delta.x || delta.y || delta.z)) {
      const currentT = sel.translate || { x: 0, y: 0, z: 0 }
      const newT = posAdd(currentT, delta)
      setUi((prev: any) => ({ ...prev, voxelSelection: { ...sel, translate: newT } }))
    }
  },

  'selection.change.selectInBounds': (ctx, event) => {
    const { dispatchEvents } = ctx
    const [, boundsSpec, opts] = event as any
    const op: 'replace' | 'add' | 'subtract' = (opts?.op || 'replace')
    const { min, max } = boundsSpec
    dispatchEvents([
      ['selection.change', { op, source: { kind: 'rect', min, max } } as any]
    ])
  },

  'selection.change.clear': (ctx) => {
    let { ui, setUi } = ctx
    ui = flushPendingTransforms(ctx, 'replace', ui)
    setUi(clearSelectionUiState)
  },

  // Delete the currently selected voxels from the document.
  // If the selection originated from the base layer, subtract its original voxels.
  // If the selection is a 'new' selection (not yet placed), simply clear it.
  'doc.selection.delete': (ctx) => {
    let { base, ui } = ctx
    // Flush pending transforms/extrusions
    ui = flushPendingTransforms(ctx, 'replace', ui)
    const sel = ui.voxelSelection
    if (sel) {
      const keys = Array.from(sel.keys || []) as number[]
      if (keys.length > 0) blendMutable(base, keys, 'subtract')
    }
    // Also delete face selection plates if present
    const faceSel = ui.faceSelection
    if (faceSel && faceSel.keys && faceSel.keys.size > 0) {
      blendMutable(base, Array.from(faceSel.keys) as number[], 'subtract')
    }

    ctx.setUi(clearSelectionUiState)
  },

  // Select exact positions (union) as a new selection
  'selection.change.selectPositions': (ctx, event) => {
    const { dispatchEvents } = ctx
    const [, positions, opts] = event as any
    const op: 'replace' | 'add' | 'subtract' = (opts?.op || 'replace')
    dispatchEvents([
      ['selection.change', { op, source: { kind: 'positions', positions } } as any]
    ])
  },

  // ========================
  // Unified face selection event
  // ========================
  'selection.change.face': (ctx, event) => {
    const { ui: uiIn, setUi } = ctx
    const [, payload] = event as any
    const { op, face, keys } = payload as { op: 'set' | 'add' | 'remove' | 'clear', face?: Face, keys?: ReadonlyArray<number> }
    const ui = flushPendingTransforms(ctx, 'replace', uiIn)
    const existing = ui.faceSelection
    if (op === 'clear') {
      setUi((prev: any) => ({ ...prev, faceSelection: null }))
      return
    }
    if (!face) { return }
    if (op === 'set') {
      const keySet = new Set<number>(keys || [])
      setUi((prev: any) => ({ ...prev, voxelSelection: null, faceSelection: { face, keys: keySet, offset: 0 } }))
      return
    }
    if (op === 'add') {
      if (!existing || existing.face !== face) {
        const keySet = new Set<number>(keys || [])
        setUi((prev: any) => ({ ...prev, voxelSelection: null, faceSelection: { face, keys: keySet, offset: 0 } }))
        return
      }
      const merged = new Set<number>(existing.keys)
      for (const k of (keys || [])) merged.add(k)
      setUi((prev: any) => ({ ...prev, voxelSelection: null, faceSelection: { face, keys: merged, offset: existing.offset || 0 } }))
      return
    }
    if (op === 'remove') {
      if (!existing || existing.face !== face) { return }
      const next = new Set<number>(existing.keys)
      for (const k of (keys || [])) next.delete(k)
      setUi((prev: any) => ({ ...prev, voxelSelection: null, faceSelection: next.size > 0 ? { face, keys: next, offset: existing.offset || 0 } : null }))
      return
    }
  },

  // ========================
  // Extrude from explicit face keys
  // ========================
  'voxel.extrudeFaces': (ctx, event) => {
    const { base } = ctx
    const [, payload] = event as any
    const { face, keys, offset, fillMode } = payload as { face: Face, keys: ReadonlyArray<number>, offset: number, fillMode?: boolean }
    if (!offset) { return }
    const keySet = new Set<number>(keys)
    const bounds = ctx.meta.bounds
    const { usedOffset } = applyExtrusionFromKeys(base, base, keySet, face, offset, bounds, fillMode ? 'fill' : 'place')
    // Advance face selection keys by offset along face dir (keep same shape)
    const n = faceToNormal(face)
    const dx = Math.sign(n.x) | 0
    const dy = Math.sign(n.y) | 0
    const dz = Math.sign(n.z) | 0
    // Advance by the effective offset (already clamped to bounds).
    const adv = (usedOffset || 0)
    // Build advanced keys, clamping to bounds
    const advanced = new Set<number>()

    for (const key of keySet) {
      const x = parseKeyX(key)
      const y = parseKeyY(key)
      const z = parseKeyZ(key)
      const nx = Math.max(0, Math.min(bounds.x - 1, x + dx * adv))
      const ny = Math.max(0, Math.min(bounds.y - 1, y + dy * adv))
      const nz = Math.max(0, Math.min(bounds.z - 1, z + dz * adv))
      advanced.add(posKeyXYZ(nx, ny, nz))
    }
    // Update face selection with clamped keys
    ctx.setUi((prev: any) => ({ ...prev, faceSelection: { face, keys: advanced, offset: 0 } }))
  },

  // ========================
  // Commit current selection transform to base and rebase selection
  // ========================
  'doc.selection.commit': (ctx, event) => {
    const { base, ui, setUi } = ctx
    const sel = ui.voxelSelection
    if (!sel || !sel.keys) { return }
    // Support explicit delta payload for durable undo/redo
    const [, payloadDelta] = event as any
    const delta = payloadDelta || sel.translate
    if (!transformExists(delta)) { return }
    // Compute moved clip and abort commit if it would place nothing (prevents accidental deletion when dragging fully out of bounds)
    const movedClip = offsetVoxels(sel.keys, delta || { x: 0, y: 0, z: 0 }, ctx.meta.bounds, base)
    if (movedClip.size === 0) {
      // No-op commit: clear transform, keep selection and base intact
      setUi((prev: any) => ({ ...prev, voxelSelection: { ...sel, translate: null } }))
      return
    }
    // Apply to base (subtract original if from base, then place moved)
    applySelectionToBaseMutable(
      base,
      { keys: sel.keys, translate: delta } as any,
      ctx.meta.bounds,
      { bounds: ctx.meta.bounds, mirrors: (ctx.meta as any).mirrors || {} }
    )
    // Rebase selection to moved clip
    const t = delta || { x: 0, y: 0, z: 0 }
    const newKeys = Array.from(shiftKeys(sel.keys, t, ctx.meta.bounds))
    const bbox = getBoundingBoxFromKeys(newKeys)
    const center = calculateBoundingBoxCenter(bbox)
    setUi((prev: any) => ({
      ...prev,
      voxelSelection: bbox && center ? {
        keys: newKeys,
        center,
        translate: null,
        boundingBox: bbox
      } : null
    }))
  },

  // Unified selection change (formerly selection.change.apply)
  'selection.change': (ctx, event) => {
    const { ui: uiIn } = ctx
    const [, payload] = event as any
    const op: 'replace' | 'add' | 'subtract' = payload?.op || 'replace'
    const source = payload?.source as { kind: string }
    if (!source || !source.kind) { return }

    let ui = flushPendingTransforms(ctx, op, uiIn)
    const combinedKeys = op === 'replace' ? new Set<number>() : new Set<number>(ui.voxelSelection?.keys || [])
    applySelectionSource(ctx, { source: source as any, op }, combinedKeys)
    finalizeSelection(ctx, ui, combinedKeys)
  },

  // Extrude along the currently selected face by an integer offset
  'voxel.extrude': (ctx, event) => {
    const { base } = ctx
    const [, params] = event as any
    const { anchor, face, offset, fillMode } = params as { anchor: Position; face: Face; offset: number; fillMode?: boolean }
    if (!offset) { return }
    const bounds = ctx.meta.bounds
    const usedOffset = applyExtrusionFromFace(base, base, anchor, face, offset, bounds, true, fillMode ? 'fill' : 'place').usedOffset

    // Advance by the effective offset (already clamped to bounds).
    const newAnchor = advanceAnchor(anchor, face, usedOffset)
    // Clamp the new anchor to stay within bounds
    const clampedAnchor = {
      x: Math.max(0, Math.min(bounds.x - 1, newAnchor.x)),
      y: Math.max(0, Math.min(bounds.y - 1, newAnchor.y)),
      z: Math.max(0, Math.min(bounds.z - 1, newAnchor.z))
    }
    ctx.setUi((prev: any) => ({ ...prev, faceSelection: { anchor: clampedAnchor, face, offset: 0 } as any }))
  },

  // ========================
  // Painting intents
  // ========================
  'paint.stroke': (ctx, event) => {
    const { base, meta } = ctx
    const [, { spine, paint, scribble, voxel, seedNormal }] = event as PaintStroke
    const applyColor = (paint?.applyColor !== false)
    const applyType = (paint?.applyType !== false)

    const out = computePaintFromSpine({
      spine: spine as any,
      base,
      bounds: ctx.meta.bounds,
      paint: paint as PaintSettings,
      scribble: scribble as ScribbleSettings,
      voxel,
      seedNormal,
      visited: new Map<number, number>(),
      applyColor,
      applyType,
    })
    blendMutable(base, out, 'paint', { bounds: meta.bounds, mirrors: (meta as any).mirrors || {} })
  },

  'paint.bucket': (ctx, event) => {
    const { base, meta } = ctx
    const [, payload] = event as any
    const { anchor, paint, voxel, seedNormal } = payload as { anchor: Position, paint: PaintSettings, voxel: VoxelData, seedNormal?: Position }
    const applyColor = (paint?.applyColor !== false)
    const applyType = (paint?.applyType !== false)
    const out = computePaintBucket({
      anchor,
      base,
      bounds: ctx.meta.bounds,
      paint,
      voxel,
      seedNormal,
      applyColor,
      applyType,
      matchSameType: true,
    })
    blendMutable(base, out, 'paint', { bounds: meta.bounds, mirrors: (meta as any).mirrors || {} })
  },

  'paint.rectangle': (ctx, event) => {
    const { base } = ctx
    const [, params] = event as ['paint.rectangle', { min: Position; max: Position; normal?: Position; mode: 'place' | 'fill' | 'subtract'; voxel: VoxelData }]
    const { min, max, normal, mode, voxel } = params

    // Resolve voxel data with surface orientation if applicable
    let voxelData = voxel
    if (normal && (usesSurfaceOrientation(voxel.type) || placementPolicy(voxel.type).target === 'surface')) {
      voxelData = { ...voxel, face: normalToFace(normal) }
    }

    rectangleVoxels(min, max, voxelData as any, base, mode as any, ctx.meta.bounds)

  },

  // ========================
  // Scribble/pen/eraser via spine expansion
  // ========================
  'scribble.apply': scribbleApply,
  // Treat scribble.append the same as scribble.apply (draft stages use append; commit rewrites to apply)
  'scribble.append': scribbleApply,
  // Swept brush (plan S9): brush.stroke / brush.stroke.append fold capsule segments onto the base
  ...brushStrokeHandlers,
  // Single-purpose worlds (src/experiences): `experience.<world>.<verb>`
  ...pageEventHandlers,

  // ========================
  // Tool events
  // ========================
  // removed ui.tool.color — palette holds the authoritative color

  'ui.setTool': (ctx, event) => {
    const { setUi } = ctx
    const [, tool] = event as ['ui.setTool', ToolType]
    setUi((prev: any) => {
      const nextTool = { ...prev.tool, currentTool: tool }
      // Ensure a concrete shape is selected when entering Shape tool
      if (tool === 'shape' && !nextTool.selectedShape) {
        nextTool.selectedShape = 'sphere'
      }
      // If shapeState exists and we're switching to shape, keep its type in sync
      const patch: any = { ...prev, tool: nextTool }
      if (tool === 'shape' && prev.shapeState && prev.shapeState.type != null) {
        patch.shapeState = { ...prev.shapeState, type: prev.shapeState.type || nextTool.selectedShape || 'sphere' }
      }
      // Single-subject rule: if leaving Select tool, drop selection as subject
      if (tool !== 'select' && prev.voxelSelection) {
        patch.voxelSelection = null
        patch.faceSelection = null
        if (prev?.activeSubjectRef?.kind === 'selection') patch.activeSubjectRef = null
      }
      // If entering Select tool with an existing selection, promote it
      if (tool === 'select' && prev.voxelSelection) {
        patch.activeSubjectRef = { kind: 'selection', id: selectionSubjectId as any }
      }
      patch.paletteOverrides = {}
      return patch
    })
  },

  'ui.tool.fillMode': (ctx, event) => {
    const { ui, setUi } = ctx
    const [, fillMode] = event as ['ui.tool.fillMode', boolean]
    const prevRect = ui.tool.rectangle || {}
    setUi((prev: any) => ({ ...prev, tool: { ...prev.tool, rectangle: { ...prevRect, fillMode } } }))
  },

  'ui.tool.paletteIndex': (ctx, event) => {
    const { setUi, meta } = ctx
    const [, index] = event as ['ui.tool.paletteIndex', number]
    setUi((prev: any) => {
      const nextTool = { ...prev.tool, paletteIndex: index }
      const basePalette = Array.isArray((meta as any)?.palette) ? (meta as any).palette : []
      const source = basePalette[index]
      const preset = source?.preset
      if (preset && typeof preset === 'object') {
        if (preset.scribble && typeof preset.scribble === 'object') {
          nextTool.scribble = { ...nextTool.scribble, ...preset.scribble }
        }
        if (preset.paint && typeof preset.paint === 'object') {
          nextTool.paint = { ...nextTool.paint, ...preset.paint }
        }
      }
      return { ...prev, tool: nextTool }
    })
  },

  'ui.tool.selectedShape': (ctx, event) => {
    const { ui, setUi } = ctx
    const [, shape] = event as ['ui.tool.selectedShape', 'sphere' | 'cylinder' | 'cone' | null]
    const updated: any = { ...ui, tool: { ...ui.tool, selectedShape: shape } }
    if (ui.tool.currentTool === 'shape' && ui.shapeState) {
      updated.shapeState = { ...ui.shapeState, type: shape || 'sphere' }
    }
    setUi(() => updated)
  },

  // ========================
  // Palette events (durable)
  // ========================
  'palette.setItems': ({ setMeta }, event) => {
    const [, items] = event as any
    setMeta((prev: any) => ({ ...prev, palette: Array.isArray(items) ? items : [] }))
  },
  'palette.setItem': ({ setMeta }, event) => {
    const [, index, item] = event as any
    setMeta((prev: any) => {
      const items = Array.isArray(prev?.palette) ? Array.from(prev.palette) : []
      items[index] = item
      return { ...prev, palette: items }
    })
  },
  'palette.setItemColor': ({ setMeta }, event) => {
    const [, index, color] = event as any
    setMeta((prev: any) => {
      const items = Array.isArray(prev?.palette) ? Array.from(prev.palette) : []
      const current = items[index] as VoxelData
      if (current) items[index] = { ...current, color }
      return { ...prev, palette: items }
    })
  },

  'ui.setBackgroundColor': ({ setUi }, event) => {
    const [, color] = event as any
    setUi((prev: any) => ({ ...prev, backgroundColor: color }))
  },

  // ========================
  // Meta events (persisted settings separate from ephemeral UI)
  // ========================
  'meta.set': ({ setMeta }, event) => {
    const [, patch] = event as any
    setMeta((prev: any) => ({ ...prev, ...patch }))
  },
  'meta.setIn': ({ setMeta }, event) => {
    const [, path, value] = event as any
    setMeta((prev: any) => {
      const next: any = { ...prev }
      let cur: any = next
      for (let i = 0; i < path.length - 1; i++) {
        const k = path[i]
        if (!(k in cur) || typeof cur[k] !== 'object') cur[k] = {}
        else cur[k] = { ...cur[k] }
        cur = cur[k]
      }
      cur[path[path.length - 1]] = value
      return next
    })
  },

  // ========================
  // Geometry events
  // ========================

  'geom.shape': (ctx, event) => {
    const { base, meta } = ctx
    const [, params] = event as any
    const { type, center, radius, height, axis, axisSign = 1, voxel, rotation } = params
    rasterizeShape(
      type,
      { center, radius, height, axis, axisSign, voxel, rotation },
      ctx.meta.bounds,
      writeFn('place', base, { bounds: meta.bounds, mirrors: (meta as any).mirrors || {} })
    )
  },

  'geom.translate': (ctx, event) => {
    const { base } = ctx
    const [, _layerId, params] = event as any
    const { dx, dy, dz } = params
    if (!base || base.size === 0) {
      return
    }
    if (isChunkedVoxelGrid(base)) {
      // Chunk-aware path: whole-chunk offsets share chunk refs, other offsets copy ids without
      // re-interning. Voxels leaving the bounds are dropped (the legacy path wraps their Morton keys).
      // setBase records journal candidates from the chunk-ref diff between the old and new base.
      ctx.setBase(translateGrid(base, dx, dy, dz))
      return
    }
    const translated = new Map<number, VoxelData>()
      ; (base as any).forEach((voxel: VoxelData, key: number) => {
        recordMutationCandidate(key)
        const x = parseKeyX(key)
        const y = parseKeyY(key)
        const z = parseKeyZ(key)
        const nextKey = posKeyXYZ(x + dx, y + dy, z + dz)
        recordMutationCandidate(nextKey)
        translated.set(nextKey, voxel)
      })
    // Replace working base with translated map (mutating where possible)
    if (typeof (base as any).clear === 'function') (base as any).clear()
    translated.forEach((v, k) => (base as any).set(k, v))
  },

  // ========================
  // UI events (no version increment for pure UI)
  // ========================

  'ui.update': ({ setUi }, event) => {
    const [, updater] = event as any
    setUi(updater)
  },

  'ui.set': ({ setUi }, event) => {
    const [, patch] = event as any
    setUi((prev: any) => ({ ...prev, ...patch }))
  },

  'ui.setIn': ({ setUi }, event) => {
    const [, path, value] = event as any
    setUi((prev: any) => {
      const newState: any = { ...prev }
      let current: any = newState
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i]
        if (!(key in current) || typeof current[key] !== 'object') current[key] = {}
        else current[key] = { ...current[key] }
        current = current[key]
      }
      current[path[path.length - 1]] = value
      return newState
    })
  },
}

export function applyWithRegistry(ctx: HandlerCtx, event: EventTuple) {
  const type = event[0] as string
  const fn = handlers[type]
  if (fn) return fn(ctx, event)
  console.warn(`Unhandled event in registry: ${type}`)
  return ctx
}
